import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lang = i18n.language || 'en';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    // If we are not on home page, navigate to home with hash, else just scroll
    if (!/^\/(?:[a-z]{2})\/?$/.test(location.pathname)) {
      navigate(`/${lang}#${id}`);
      // After navigation, allow render then scroll
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_enterprise-portal-38/artifacts/cfcjda9k_IMG-20250104-WA0003.jpg" 
              alt="GO VIP - Conforto & Privacidade" 
              className="h-14 md:h-16 w-auto object-contain"
              style={{ maxWidth: '200px', objectFit: 'contain' }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-home"
            >
              {t('header.home')}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-services"
            >
              {t('header.services')}
            </button>
            <Link
              to={`/${lang}/porto-airport-private-transfer`}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-airport-transfer"
            >
              {t('header.airport_transfer')}
            </Link>
            <Link
              to={`/${lang}/tours`}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-tours"
            >
              {t('footer.tours')}
            </Link>
            <button
              onClick={() => scrollToSection('booking')}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-booking"
            >
              {t('header.booking')}
            </button>
            <Link
              to={`/${lang}/blog`}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-blog"
            >
              {t('header.blog')}
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-amber-700 transition-colors font-medium"
              data-testid="nav-contact"
            >
              {t('header.contact')}
            </button>
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher variant="flags" />
            <a href="tel:+351932651940">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="header-phone-btn">
                <Phone className="w-4 h-4 mr-2" />
                {t('header.phone')}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-home"
              >
                {t('header.home')}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-services"
              >
                {t('header.services')}
              </button>
              <Link
                to={`/${lang}/porto-airport-private-transfer`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-airport-transfer"
              >
                {t('header.airport_transfer')}
              </Link>
              <button
                onClick={() => scrollToSection('destinations')}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-destinations"
              >
                {t('header.destinations')}
              </button>
              <button
                onClick={() => scrollToSection('booking')}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-booking"
              >
                {t('header.booking')}
              </button>
              <Link
                to={`/${lang}/blog`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-blog"
              >
                {t('header.blog')}
              </Link>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-700 hover:text-amber-700 transition-colors font-medium text-left px-4"
                data-testid="mobile-nav-contact"
              >
                {t('header.contact')}
              </button>
              <div className="px-4 pt-2 flex justify-center">
                <LanguageSwitcher variant="dropdown" />
              </div>
              <div className="px-4 pt-2">
                <a href="tel:+351932651940">
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="mobile-phone-btn">
                    <Phone className="w-4 h-4 mr-2" />
                    {t('header.phone')}
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
